import { ref, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

interface VersionInfo {
  version: string
  buildTime: string
}

const POLL_INTERVAL_MS = 5 * 60 * 1000

export function useVersionCheck() {
  const updateAvailable = ref(false)
  let baselineVersion: string | null = null
  let intervalId: ReturnType<typeof setInterval> | null = null
  let unregisterGuard: (() => void) | null = null

  async function check() {
    try {
      const response = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' })
      if (!response.ok) return
      const info: VersionInfo = await response.json()
      if (!baselineVersion) {
        baselineVersion = info.version
        return
      }
      if (info.version !== baselineVersion) {
        updateAvailable.value = true
      }
    } catch {
      // version.json not available — skip silently
    }
  }

  function startPolling() {
    check()
    intervalId = setInterval(check, POLL_INTERVAL_MS)
    const router = useRouter()
    unregisterGuard = router.afterEach(() => {
      check()
    })
  }

  function stopPolling() {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (unregisterGuard) {
      unregisterGuard()
      unregisterGuard = null
    }
  }

  onUnmounted(() => {
    stopPolling()
  })
  return { updateAvailable, startPolling, stopPolling }
}
